import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { AreaOfInterest, DoBChart, SurveyDoBChart } from '@/server/MakeSenseApp-backend'
import { ref, provide, watch } from 'vue'

import { use } from 'echarts/core'
import VChart, { THEME_KEY } from 'vue-echarts'
import {
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
  MarkAreaComponent,
  DataZoomComponent,
  LegendComponent
} from 'echarts/components'
import { LineChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'
import { UniversalTransition } from 'echarts/features'


export default /*@__PURE__*/_defineComponent({
  __name: 'DoBChart',
  props: {
    data: {},
    isLoading: { type: Boolean },
    showLegend: { type: Boolean },
    areaOfInterest: {},
    chartId: {},
    showDelta: { type: Boolean }
  },
  setup(__props: any) {

use([
  TooltipComponent,
  GridComponent,
  VisualMapComponent,
  LineChart,
  CanvasRenderer,
  MarkAreaComponent,
  UniversalTransition,
  DataZoomComponent,
  LegendComponent
])

provide(THEME_KEY, 'light')

let colors = ['#FF5C00', '#A01C00', '#00215F', '#D70D00', '#FFC107', '#457D3C']

var props = __props

function setLegend(data: DoBChart | SurveyDoBChart) {
  if (data instanceof DoBChart) {
    return ['Cable', 'Seabed']
  } else if (data instanceof SurveyDoBChart) {
    return (props.showDelta ? data.seriesDoB : data.seriesCable).map((x) => x.name)
  }
}

function setSeries(data: DoBChart | SurveyDoBChart) {
  if (data instanceof DoBChart) {
    return [
      {
        name: 'Cable',
        data: props.showDelta ? data.doB : data.cable,
        markArea: props.areaOfInterest
          ? {
              itemStyle: {
                color: 'rgba(255, 173, 177, 0.4)'
              },
              data: [
                [
                  {
                    xAxis: props.areaOfInterest.minKP
                  },
                  {
                    xAxis: props.areaOfInterest.maxKP
                  }
                ]
              ]
            }
          : undefined,
        type: 'line',
        symbol: 'none',
        lineStyle: {
          color: '#FF5C00',
          width: 3
        },
        itemStyle: {
          color: '#FF5C00'
        }
      },
      {
        name: 'Seabed',
        data: data.seabed,
        markArea: props.areaOfInterest
          ? {
              itemStyle: {
                color: 'rgba(255, 173, 177, 0.4)'
              },
              data: [
                [
                  {
                    xAxis: props.areaOfInterest.minKP
                  },
                  {
                    xAxis: props.areaOfInterest.maxKP
                  }
                ]
              ]
            }
          : undefined,
        type: 'line',
        symbol: 'none',
        areaStyle: {
          origin: 'end'
        },
        lineStyle: {
          color: '#06A3DA',
          width: 3
        },
        emphasis: {
          disabled: true
        },
        itemStyle: {
          color: '#C1E8F6'
        }
      }
    ].filter((series) => props.showDelta == false || series.name !== 'Seabed')
  } else if (data instanceof SurveyDoBChart) {
    return (props.showDelta ? data.seriesDoB : data.seriesCable)?.map((x, i) => {
      if (x.name === 'Seabed') {
        return {
          name: x.name,
          data: x.values,
          type: 'line',
          symbol: 'none',
          areaStyle: {
            origin: 'end'
          },
          lineStyle: {
            color: '#06A3DA',
            width: 3
          },
          emphasis: {
            disabled: true
          },
          itemStyle: {
            color: '#C1E8F6'
          }
        }
      } else {
        return {
          name: x.name,
          data: x.values,
          type: 'line',
          symbol: 'none',
          lineStyle: {
            color: colors[(props.showDelta ? i : i - 1) % colors.length],
            width: 3
          },
          itemStyle: {
            color: colors[(props.showDelta ? i : i - 1) % colors.length]
          }
        }
      }
    })
  }
}

let option = ref<any>()

watch(
  () => [props.data, props.showDelta],
  () => {
    option.value = {
      animationDuration: 100,
      xAxis: {
        type: 'value',
        min: function (value: any) {
          return value.min - 1 < 0 ? 0 : Math.round(value.min - 1)
        },
        max: function (value: any) {
          return Math.round(value.max + 1)
        },
        name: 'Kilometer Point (KP)',
        nameLocation: 'middle',
        nameGap: 30,
        nameTextStyle: {
          fontSize: 12,
          align: 'center',
          color: 'black',
          fontFamily: 'roboto'
        }
      },
      yAxis: {
        type: 'value',
        name: props.showDelta ? 'Depth of burial (m)' : 'Seabed (m)',
        nameLocation: 'middle',
        nameGap: 40,
        nameTextStyle: {
          fontSize: 12,
          align: 'center',
          color: 'black',
          fontFamily: 'roboto'
        }
      },
      dataZoom: [
        {
          type: 'inside'
        }
      ],
      tooltip: {
        trigger: 'axis'
      },
      legend: {
        data: props.data && props.showLegend ? setLegend(props.data) : [],
        textStyle: {
          fontSize: 14,
          color: 'black',
          fontFamily: 'roboto'
        }
      },
      series: props.data
        ? setSeries(props.data)
        : [
            {
              name: 'Cable',
              data: [],
              type: 'line',
              symbol: 'none',
              lineStyle: {
                color: '#FF5C00',
                width: 3
              },
              itemStyle: {
                color: '#FF5C00'
              }
            },
            {
              name: 'Seabed',
              data: [],
              type: 'line',
              symbol: 'none',
              areaStyle: {
                origin: 'end'
              },
              lineStyle: {
                color: '#06A3DA',
                width: 3
              },
              emphasis: {
                disabled: true
              },
              itemStyle: {
                color: '#C1E8F6'
              }
            }
          ]
    }
  }
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(VChart), {
    id: _ctx.chartId,
    loading: _ctx.isLoading === true || _ctx.data === undefined,
    class: "chart",
    option: _unref(option),
    autoresize: ""
  }, null, 8, ["id", "loading", "option"]))
}
}

})